import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, FileText } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useWorkspace } from '@/hooks/useWorkspace';
import { showToast } from '@/components/ui/toaster';
import { payrollService } from '@/services/payroll.service';
import { formatRp } from '@/lib/currency';

const STATUS_LABEL_KEY: Record<string, string> = {
    draft: 'payslip.status.draft',
    finalized: 'payslip.status.finalized',
    paid: 'payslip.status.paid',
};

export default function PayslipPage() {
    const { t } = useI18n();
    const { currentWorkspace } = useWorkspace();
    const navigate = useNavigate();
    const [list, setList] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!currentWorkspace) return;
        const enabled = currentWorkspace.enabledPlugins?.includes('payroll');
        if (!enabled) {
            setLoading(false);
            return;
        }
        load();
    }, [currentWorkspace?._id]);

    const load = async () => {
        if (!currentWorkspace) return;
        setLoading(true);
        try {
            const res = await payrollService.listPayslips(currentWorkspace._id);
            setList((res.data as any[]) || []);
        } catch (e: any) {
            showToast({ variant: 'destructive', description: e?.response?.data?.error || 'Gagal memuat' });
        } finally {
            setLoading(false);
        }
    };

    if (!currentWorkspace) {
        return <div className="px-4 pt-12 max-w-md mx-auto text-center text-sm text-gray-500">{t('common.loading')}</div>;
    }

    const pluginEnabled = currentWorkspace.enabledPlugins?.includes('payroll');
    if (!pluginEnabled) {
        return (
            <div className="px-4 pt-12 max-w-md mx-auto text-center">
                <div className="h-14 w-14 mx-auto rounded-full flex items-center justify-center bg-amber-100 mb-3">
                    <Wallet className="h-6 w-6 text-amber-600" />
                </div>
                <p className="text-sm font-bold text-gray-800">Plugin Payroll belum aktif</p>
                <p className="text-xs text-gray-500 mt-1">Slip gaji akan muncul setelah owner workspace aktifkan plugin Payroll.</p>
            </div>
        );
    }

    return (
        <div className="px-4 pt-3 max-w-md mx-auto space-y-3">
            <header className="pt-2 mb-2 flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Wallet className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                    <h1 className="text-lg font-black text-gray-900">{t('payslip.title')}</h1>
                    <p className="text-[11px] text-gray-500 max-w-[260px] leading-tight">{t('payslip.subtitle')}</p>
                </div>
            </header>

            {loading && <p className="text-sm text-gray-400 italic px-1">{t('common.loading')}</p>}

            {!loading && list.length === 0 && (
                <div className="bg-white rounded-2xl p-8 text-center" style={{ border: '1px dashed #e0d6cc' }}>
                    <div className="h-12 w-12 mx-auto rounded-full bg-purple-100 flex items-center justify-center mb-3">
                        <FileText className="h-5 w-5 text-purple-600" />
                    </div>
                    <p className="text-sm font-bold text-gray-700">{t('payslip.empty')}</p>
                    <p className="text-xs text-gray-500 mt-1 max-w-[260px] mx-auto">{t('payslip.empty_hint')}</p>
                </div>
            )}

            <div className="space-y-2">
                {list.map((p) => (
                    <button
                        key={p.id}
                        onClick={() => navigate(`/slip/${p.id}`)}
                        className="w-full text-left bg-white rounded-2xl p-4 hover:bg-gray-50 transition flex items-center gap-3"
                        style={{ border: '1px solid #e0d6cc' }}
                    >
                        <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                            <FileText className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm text-gray-900">{p.period}</p>
                            <p className="text-[11px] text-gray-500 mt-0.5">{t(STATUS_LABEL_KEY[p.status] || p.status)}</p>
                        </div>
                        <span className="text-base font-black text-gray-900">{formatRp(p.netSalary)}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}
